import { useState, useEffect, useCallback } from 'react'
import WebService from '@util/webService'

/**
 * 심볼 id -> 이름 사전과, **두 화면이 같이 쓰는 표기 규칙** 하나.
 *
 * [이름 붙이기, 2026-08-27 주인 지시] 신호 화면과 지갑 화면이 `symbolId 1`, `#3` 처럼 숫자만
 * 그렸다. `# 1 (BTCUSDT)` 로 바꾼다.
 *
 * == 왜 훅으로 빼나 ==
 *
 * 화면이 둘이고 **표기 규칙은 하나여야 한다.** 각자 문자열을 조립하면 한쪽만 바꿨을 때 두
 * 화면이 서로 다른 이름을 그리고, 그건 같은 자리를 두 이름으로 부르는 것과 같다.
 * 조회도 같이 딸려 온다 -- 사전 없이 규칙만 나눌 수는 없다.
 *
 * == 왜 캐시를 안 두나 ==
 *
 * 모듈 수준에 한 번 받아 재사용하는 길이 있지만 **안 둔다.** `symbols` 는 배선을 바꿀 때만
 * 늘고, 그때 새로고침이 없으면 새 심볼이 영영 이름 없이 그려진다. 화면 진입마다 한 번은
 * 자산 수 만큼의 행을 받는 비용이고, 그건 이 화면들의 폴링 한 회전보다 싸다.
 *
 * == 실패하면 비운 채로 둔다 ==
 *
 * 못 받으면 `symbolTextOf` 가 **숫자만** 돌려준다(`# 1`). 지금 화면 그대로다. 이름은 읽기
 * 편하라고 붙이는 것이지 신원이 아니므로, 못 받았다고 화면을 막거나 자리를 지울 이유가 없다.
 */
const useSymbolNames = () => {
  /** `{ '1': 'BTCUSDT', ... }` -- 키가 **문자열**이다. 서버는 숫자 id를 주고 화면은 그걸
   * 문자열로도 숫자로도 들고 있어서(`sy.symbolId`는 숫자, 맵 키는 문자열), 넣을 때와 찾을
   * 때 양쪽에서 `String()`으로 맞춘다. */
  const [names, set_names] = useState({})

  useEffect(() => {
    let alive = true
    const load = async () => {
      const resp = await WebService.get('/symbol')
      if (!alive) {
        return
      }
      if (resp.repCode !== 'ack') {
        // 화면을 막을 일은 아니다 -- 이름만 안 붙고 나머지는 다 돈다.
        console.error('심볼 목록 조회 실패=>', resp.repMessage)
        return
      }
      const repMessage = JSON.parse(resp.repMessage)
      const records = JSON.parse(repMessage.records || '[]')
      const map = {}
      records.forEach((r) => {
        map[String(r.id)] = r.symbol
      })
      set_names(map)
    }
    load()
    return () => { alive = false }
  }, [])

  /**
   * `# 1 (BTCUSDT)`. 이름을 모르면 **숫자만** 돌려준다.
   *
   * 모를 때 `# 1 (?)` 이나 `# 1 (알 수 없음)` 으로 채우지 않는다 -- 그건 "이 심볼에 문제가 있다"
   * 로 읽히는데 사실은 사전을 아직 못 받았을 뿐이다. 숫자는 어느 쪽이든 맞다.
   */
  const symbolTextOf = useCallback((symbolId) => {
    const name = names[String(symbolId)]
    if (!name) {
      return `# ${symbolId}`
    }
    return `# ${symbolId} (${name})`
  }, [names])

  return { names, symbolTextOf }
}

export default useSymbolNames
